import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Callout — a boxed line with an icon and optional actions (T-0186).
 * One component for the prototype's inline notes, by tone:
 *
 *   tip    `.tip`        paper-50 hint ("Say it out loud when you change subject")
 *   warn   `.dup-note`, `.why`  at-risk tint ("Possible duplicate of …", "can't be confirmed")
 *   ask    `.match`      amber question that must be answered ("Is this Robert Chen?")
 *   ok     `.match.on`   the answer, in green ("Robert Chen · already in this engagement")
 *   ai     `.wait-line`  AI surface ("2 more items are waiting for review")
 *   note   `.alert`      a record note with a left rule ("Cancelled by …", "Retired by …")
 *   form   `.inline-form` a small form opened in place ("Cancel S5: why it won't happen")
 *
 * Source: `docs/discovery/prototype/discovery.css` and `docs/requirement-library/prototype/tokens.css`.
 */

export type CalloutTone = 'tip' | 'warn' | 'ask' | 'ok' | 'ai' | 'note' | 'form';

const TONE: Record<CalloutTone, { box: string; icon: string }> = {
  tip: { box: 'border border-ink-100 bg-paper-50 text-meta text-ink-700', icon: 'text-ink-500' },
  warn: { box: 'bg-posture-at-risk-tint text-body-sm text-ink-700', icon: 'text-posture-at-risk' },
  ask: {
    box: 'border border-accent-amber-100 bg-accent-amber-50 text-body-sm text-ink-700',
    icon: 'text-accent-amber-700',
  },
  ok: {
    box: 'border border-posture-healthy bg-posture-healthy-tint text-body-sm text-ink-700',
    icon: 'text-posture-healthy',
  },
  ai: {
    box: 'border border-ai-surface-border bg-ai-surface text-body-sm text-ai-ink',
    icon: 'text-ai-ink',
  },
  note: {
    box: 'border border-l-4 border-ink-100 border-l-posture-unassessed bg-paper-0 text-body-sm text-ink-700',
    icon: 'text-ink-500',
  },
  form: {
    box: 'border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-paper-0 px-[18px] py-4 text-body-sm text-ink-700',
    icon: 'text-accent-amber-700',
  },
};

export interface CalloutProps {
  tone: CalloutTone;
  icon?: LucideIcon;
  children: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  role?: string;
}

export function Callout({ tone, icon: Icon, children, actions, className, role }: CalloutProps) {
  const t = TONE[tone];
  return (
    <div
      role={role}
      className={cn('flex items-start gap-2.5 rounded-card px-3 py-2.5', t.box, className)}
    >
      {Icon ? <Icon aria-hidden="true" className={cn('mt-0.5 h-4 w-4 flex-none', t.icon)} /> : null}
      <div className="min-w-0 flex-1">{children}</div>
      {actions ? (
        <div className="flex flex-none flex-wrap items-center gap-2 self-center">{actions}</div>
      ) : null}
    </div>
  );
}
